import { useCallback, useMemo, useRef, useState } from 'react';
import { useEffect } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { t } from 'i18next';
import { Trans } from 'react-i18next';

import Header from '../components/global/Header';
import ShoeCard from '../components/catalog/ShoeCard';
import Loader from '../components/global/Loader';
import MeasurementFilter from '../components/catalog/MeasurementFilter';
import PaginationBlock from '../components/global/PaginationBlock';
import FiltersForm from '../components/catalog/FiltersForm';
import FiltersButton from '../components/catalog/FiltersButton';
import { apiGetShoeModels, apiGetShoeModelsResult, apiGetShoeProperties } from '../app/catalogApi';
import { showNotification } from '../utils/notification';
import SortButton from '../components/catalog/SortButton';
import Footer from '../components/global/Footer';
import SearchForm from '../components/global/SearchForm';
import useSessionStorageState from '../utils/useSessionStorageState';
import { Button, Collapse, Form, Modal, ProgressBar, Spinner } from 'react-bootstrap';
import useDebounce from '../utils/useDebounce';
import useEmployeeApi from '../app/useEmployeeApi';
import useProfessionApi from '../app/useProfessionApi';
import EmployeeSelect from '../components/employee/EmployeeSelect';
import SeasonSelect from '../components/employee/SeasonSelect';
import MeasurementSelectDialog from '../components/measurement/MeasurementSelectDialog';
import GroupSelectDialog from '../components/measurement/GroupSelectDialog';
import useClientMeasurementApi from '../app/useClientMeasurementApi';
import MeasurementList from '../components/measurement/MeasurementList';

const Catalog = () => {
    const PAGE_LIMIT = 16;

    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const navigate = useNavigate();

    const employeeApi = useEmployeeApi();
    const professionApi = useProfessionApi();
    const clientMeasurementApi = useClientMeasurementApi();

    const [measurementId, setMeasurementId] = useSessionStorageState('catalogMeasurementId', null);
    const [humanName, setHumanName] = useSessionStorageState('catalogHumanName', null);
    const [employee, setEmployee] = useSessionStorageState('catalogEmployee', null);
    const [employeeSeason, setEmployeeSeason] = useSessionStorageState('catalogEmployeeSeason', 'summer');

    const [measurementIds, setMeasurementIds] = useSessionStorageState('catalogMeasurementIds', null);
    const [initMeasurementIds, setInitMeasurementIds] = useSessionStorageState('catalogInitMeasurementIds', null);
    const [group, setGroup] = useSessionStorageState('catalogGroup', null);
    const [groupSelected, setGroupSelected] = useSessionStorageState('catalogGroupSelected', false);

    const [measurementSelectOpen, setMeasurementSelectOpen] = useState(false);
    const [groupSelectOpen, setGroupSelectOpen] = useState(false);
    const [employeeSelectOpen, setEmployeeSelectOpen] = useState(false);

    const [measurementListOpen, setMeasurementListOpen] = useState(false);

    const [isLoading, setIsLoading] = useState(true);
    const [modelsLoading, setModelsLoading] = useState(false);

    const abortControllerRef = useRef(null);

    const [items, setItems] = useState([]);
    const [currentPage, setCurrentPage] = useSessionStorageState('catalogPage', 1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [outputUnfit, setOutputUnfit] = useSessionStorageState('catalogOutputUnfit', false);
    const [sortMode, setSortMode] = useSessionStorageState('catalogSort', (measurementId || measurementIds) ? "RATING" : "");

    const [currentTaskId, setCurrentTaskId] = useState(null);
    const [currentTaskProgress, setCurrentTaskProgress] = useState(0);

    const ratingSortEnabled = useMemo(() => {
        return measurementId || measurementIds;
    }, [measurementId, measurementIds]);

    const DEFAULT_SELECTED_PROPERTIES = {
        availability: ["В наличии", "В производстве"],
    };
    const [defaultSelected] = useState(DEFAULT_SELECTED_PROPERTIES);

    const [searchString, setSearchString] = useSessionStorageState("catalogSearchString", "");;
    const [searchStringForUpdate, setSearchStringForUpdate] = useState(searchString);
    const [shoeProperties, setShoeProperties] = useState(null);
    const [selectedProperties, setSelectedProperties] = useSessionStorageState("catalogProperties", DEFAULT_SELECTED_PROPERTIES);

    const [, setCardPlayingModelId, cardPlayingModelId] = useDebounce(null, 500);

    const dismissMeasurementFilter = useCallback(() => {
        setMeasurementId(null);
        setMeasurementIds(null);
        setInitMeasurementIds(null);
        setHumanName(null);
        setEmployee(null);

        setGroup(null);
        setGroupSelected(false);

        setSearchParams({});
        setOutputUnfit(false);
        setSortMode("");
        setCurrentPage(1);
    }, [setCurrentPage, setEmployee, setGroup, setGroupSelected, setHumanName, setInitMeasurementIds, setMeasurementId, setMeasurementIds, setOutputUnfit, setSearchParams, setSortMode]);

    const updateSelectedProperties = (newProperties) => {
        setCurrentPage(1);
        setSelectedProperties(newProperties);
    }

    function onSearchSubmit(searchString) {
        setCurrentPage(1);
        setSearchStringForUpdate(searchString);
    }

    function onFiltersClear() {
        setSearchString("");
        setSearchStringForUpdate("");
    }

    const fetchEmployee = useCallback(async (employeeId) => {
        try {
            // Fetch employee
            const employee = await employeeApi.getEmployee(employeeId);
            setEmployee(employee);

            return employee;
        } catch (err) {
            console.log(err);
            showNotification(t("employeeFetchFailed") + ": " + err, 'danger');
        }
    }, [employeeApi, setEmployee]);

    const selectMeasurement = useCallback(async (measurementData) => {
        setMeasurementId(measurementData.measurementId);
        setHumanName(measurementData?.humanInfo?.name);
        setSortMode("RATING");
        setCurrentPage(1);
    }, [setMeasurementId, setHumanName, setSortMode, setCurrentPage]);

    const selectGroup = useCallback(async (group) => {
        setGroup(group);
        setGroupSelected(true);
        setSortMode("RATING");
        setCurrentPage(1);
    }, [setCurrentPage, setGroup, setGroupSelected, setSortMode]);

    const selectEmployee = useCallback(async (employee) => {
        try {
            setEmployee(employee);

            if (employeeSeason && employee?.measurement_ids?.length > 0) {
                // Perform profession selection
                const selection = await professionApi.getEmployeeSelection(employee.id, employeeSeason);

                // Select last measurement from employee
                setMeasurementId(employee?.measurement_ids[employee?.measurement_ids.length - 1]);

                // Set human name from employee
                const humanName = [employee?.first_name, employee?.last_name].filter((item) => item).join(" ");
                setHumanName(humanName);

                // Set sort mode
                setSortMode("RATING");

                // Insert selection into state
                setCurrentPage(1);
                navigate(".", {
                    state: {
                        selectedProperties: {
                            climaticConditions: [selection.climaticCondition],
                            //riskFactors: selectedRiskFactors,
                            protectiveProperties: selection.protectiveProperties,
                        }
                    }
                });
            }
        } catch (err) {
            console.log(err);
            showNotification(t("employeeFetchFailed") + ": " + err, 'danger');
        }
    }, [employeeSeason, navigate, professionApi, setCurrentPage, setEmployee, setHumanName, setMeasurementId, setSortMode]);

    const toggleMeasurementList = useCallback(() => {
        if (measurementIds && initMeasurementIds) {
            setMeasurementListOpen(!measurementListOpen);
        }
    }, [initMeasurementIds, measurementIds, measurementListOpen]);

    const setShoeModels = useCallback((data) => {
        setModelsLoading(false);
        setIsLoading(false);
        setItems(data.shoeModels);
        setTotalPages(data.page.pageCount); // Adjust the page number to start at 1
        setTotalCount(data.page.totalCount);

        // Reset current page if it is greater than the total number of pages
        if (currentPage > data.page.pageCount) {
            if (data.page.pageCount > 0) {
                setCurrentPage(data.page.pageCount);
            } else {
                setCurrentPage(1);
            }
        }
    }, [currentPage, setCurrentPage]);

    const onContinueMany = useCallback((processedMeasurementIds) => {
        // Remove processed measurement IDs from state
        const remainingMeasurementIds = measurementIds.filter((id) => !processedMeasurementIds.includes(id));
        setMeasurementIds(remainingMeasurementIds);
        setCurrentPage(1);
    }, [measurementIds, setCurrentPage, setMeasurementIds]);

    const onFinishMany = useCallback(() => {
        dismissMeasurementFilter();

        // navigate to cart with delay (to update state)
        setTimeout(() => {
            navigate("/cart");
        }, 200);
    }, [dismissMeasurementFilter, navigate]);

    // Scroll on the top when page changes
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, [currentPage]);

    // Use data from searchParams
    useEffect(() => {
        // Employee season
        if (searchParams.get('employeeSeason')) {
            if (employeeSeason !== searchParams.get('employeeSeason')) {
                setEmployeeSeason(searchParams.get('employeeSeason'));
            }
        }

        // Employee id
        if (searchParams.get('employeeId')) {
            if (employee?.id !== searchParams.get('employeeId')) {
                fetchEmployee(searchParams.get('employeeId'))
                    .then((employee) => {
                        selectEmployee(employee);
                    });
            }
        }

        // Measurement id
        if (searchParams.get('measurementId')) {
            if (measurementId !== searchParams.get('measurementId')) {
                setMeasurementId(searchParams.get('measurementId'));
                setCurrentPage(1);
                setOutputUnfit(false);
                setSortMode("RATING");
            }
        }

        // Human name
        if (searchParams.get('humanName')) {
            if (humanName !== searchParams.get('humanName')) {
                setHumanName(searchParams.get('humanName'));
            }
        }

        // Group id
        if (searchParams.has('groupId') && clientMeasurementApi.ready()) {
            const groupId = searchParams.get('groupId') || null;
            if (group?.group_id !== groupId) {
                clientMeasurementApi.getGroup(groupId)
                    .then((group) => {
                        setGroup(group);
                        setGroupSelected(true);
                    });
            }
        }
    }, [searchParams, humanName, setCurrentPage, setHumanName, employeeSeason, setEmployeeSeason, measurementId, setMeasurementId, setSortMode, employee?.id, fetchEmployee, selectEmployee, clientMeasurementApi, group?.group_id, setGroup, setGroupSelected, setOutputUnfit]);

    // Use data from state
    useEffect(() => {
        // Search string
        if (location.state?.searchString) {
            setSearchString(location.state.searchString);
            setSearchStringForUpdate(location.state.searchString);
            location.state.searchString = null;
        }

        // Populate selected properties from given state
        if (location.state?.selectedProperties) {
            for (const [key, value] of Object.entries(location.state.selectedProperties)) {
                setSelectedProperties((prev) => ({ ...prev, [key]: [...value] }));
            }

            location.state.selectedProperties = null;
        }

        if (location.state?.processedMeasurementIds) {
            const processedMeasurementIds = location.state.processedMeasurementIds;
            location.state.processedMeasurementIds = null;
            onContinueMany(processedMeasurementIds);
        }

        if (location.state?.finishMany) {
            location.state.finishMany = null;
            onFinishMany();
        }
    }, [location, setSearchString, setSelectedProperties, setSearchStringForUpdate, onContinueMany, onFinishMany]);

    // Read measurements from the selected group
    useEffect(() => {
        if (groupSelected && clientMeasurementApi.ready() && (!measurementIds || !initMeasurementIds)) {
            clientMeasurementApi.getMeasurements({ groupId: group ? group.group_id : null, allGroups: false })
                .then(data => {
                    const mids = data.map(m => m.measurement_id);
                    setMeasurementIds(mids);
                    setInitMeasurementIds(mids);
                })
                .catch(err => {
                    console.log(err);
                    showNotification(t("Error loading measurements") + ": " + err, 'danger');
                });
        }
    }, [group, clientMeasurementApi, setMeasurementIds, groupSelected, setInitMeasurementIds, measurementIds, initMeasurementIds]);

    // Fetch properties
    useEffect(() => {
        apiGetShoeProperties()
            .then(data => {
                setShoeProperties(data);
            })
            .catch(err => {
                console.log(err);
                showNotification(t("Error loading shoe properties") + ": " + err, 'danger');
            })
    }, []);

    // Fetch shoe models
    useEffect(() => {
        // Abort previous call
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();

        // NOTE: fetch is slightly delayed to avoid unnecessary refetch after dependencies change right after component mount
        const delayedFetch = setTimeout(() => {
            setModelsLoading(true);

            apiGetShoeModels({
                limit: PAGE_LIMIT,
                currentPage: currentPage,
                measurementId: measurementId,
                measurementIds: measurementIds,
                searchString: searchStringForUpdate,
                propertiesFilter: selectedProperties,
                sortMode: sortMode,
                outputUnfit: outputUnfit,
                signal: abortControllerRef.current.signal
            })
                .then(data => {
                    if (!(("completed" in data) || data.completed) &&
                        "shoeModels" in data) {
                        setShoeModels(data);
                        setCurrentTaskId(null);
                    } else if ("taskId" in data) {
                        setCurrentTaskId(data.taskId);
                    } else {
                        throw new Error(t("catalogUnexpectedServerResponse"));
                    }
                })
                .catch(err => {
                    if (err.name === 'AbortError') {
                        console.log('Fetch request was aborted');
                    } else {
                        console.log(err);
                        showNotification(t("Error loading shoe models") + ": " + err, 'danger');
                        setModelsLoading(false);
                        setIsLoading(false);
                    }
                });
        }, 200);

        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }

            clearTimeout(delayedFetch);
        };
    }, [currentPage, measurementId, measurementIds, outputUnfit, searchStringForUpdate, selectedProperties, setShoeModels, sortMode]);

    // Periodic fetch for the current task ID
    useEffect(() => {
        if (currentTaskId) {
            const interval = setInterval(() => {
                apiGetShoeModelsResult(currentTaskId)
                    .then(data => {
                        if (!(("completed" in data) || data.completed) &&
                            "shoeModels" in data) {
                            setShoeModels(data);
                            setCurrentTaskId(null);
                        } else if ("taskId" in data) {
                            setCurrentTaskId(data.taskId);
                            setCurrentTaskProgress(data.progress);
                        } else {
                            throw new Error(t("catalogUnexpectedServerResponse"));
                        }
                    })
                    .catch(err => {
                        console.log(err);
                        showNotification(t("Error loading shoe models") + ": " + err, 'danger');
                    });
            }, 2000);
            return () => clearInterval(interval);
        }
    }, [currentTaskId, setShoeModels]);

    return (
        <>
            <Helmet>
                <title>{t("Catalog")} - FeetApp</title>
            </Helmet>

            <Header />

            <div className='container'>
                {/* Measurement info */}
                {!measurementId && (employeeApi.ready() ? (
                    <div className='mt-3 alert alert-info'>
                        <Trans>catalogSelectMeasurementAlert <Link to="/measurements">measurements</Link></Trans>
                    </div>
                ) : (
                    <div className='mt-3 alert alert-info'>
                        <Trans>Use <Link to="/measurements">measurements</Link> tab to select a measurement to fit optimal shoes, or use <a
                            href="/fitAll" onClick={() => navigate("/fitAll", { state: { selectedProperties: selectedProperties, searchString: searchString } })}
                        > fit all</a> to fit for all measurements from  the shopping list.</Trans>
                    </div>
                ))}

                <MeasurementSelectDialog
                    show={measurementSelectOpen}
                    title={t("selectMeasurement")}
                    onSelected={(measurementData) => {
                        selectMeasurement(measurementData);
                        setMeasurementSelectOpen(false);
                    }}
                    onCancel={() => setMeasurementSelectOpen(false)}
                />

                <GroupSelectDialog
                    show={groupSelectOpen}
                    title={t("selectGroup")}
                    onSelected={(group) => {
                        selectGroup(group);
                        setGroupSelectOpen(false);
                    }}
                    onCancel={() => setGroupSelectOpen(false)}
                />

                <Modal show={employeeSelectOpen} onHide={() => setEmployeeSelectOpen(false)}>
                    <Modal.Header>
                        <Modal.Title><Trans>selectEmployee</Trans></Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <SeasonSelect
                            season={employeeSeason}
                            setSeason={setEmployeeSeason} />

                        <EmployeeSelect
                            active={true}
                            handleActive={setEmployeeSelectOpen}
                            selected={employee}
                            handleSelected={(employee) => {
                                selectEmployee(employee);
                                setEmployeeSelectOpen(false);
                            }} />
                    </Modal.Body>
                </Modal>

                {/* Search form */}
                <div className='mt-3'>
                    <SearchForm searchString={searchString} setSearchString={setSearchString} onSubmit={onSearchSubmit} />
                </div>

                {/* Active filters & sort button */}
                <div className='mt-3 d-flex align-items-center'>
                    <div className='d-lg-none me-2'>
                        <FiltersButton shoeProperties={shoeProperties} selected={selectedProperties} setSelected={updateSelectedProperties} defaultSelected={defaultSelected} onClear={onFiltersClear} />
                    </div>

                    {/* Measurement plate - big screen only */}
                    {(measurementId || measurementIds) ? (
                        <div className='d-none d-lg-block'>
                            <div className='d-flex align-items-center'>
                                <MeasurementFilter
                                    measurementId={measurementId}
                                    humanName={humanName}
                                    employee={employee}
                                    season={employeeSeason}
                                    measurementIds={measurementIds}
                                    actionCallback={toggleMeasurementList}
                                    closeCallback={dismissMeasurementFilter} />

                                {/* Output unfit checkbox */}
                                <div>
                                    <Form.Check type="checkbox" checked={outputUnfit} onChange={(e) => setOutputUnfit(e.target.checked)} label={t("outputUnfit")} />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className='d-none d-lg-block'>
                            {clientMeasurementApi.ready() && (
                                <>
                                    <Button variant="primary" onClick={() => setMeasurementSelectOpen(true)}>
                                        <Trans>catalogSelectMeasurement</Trans>
                                    </Button>

                                    <Button className='ms-2' variant="primary" onClick={() => setGroupSelectOpen(true)}>
                                        <Trans>catalogSelectGroup</Trans>
                                    </Button>
                                </>
                            )}

                            {employeeApi.ready() && (
                                <Button className='ms-2' variant="primary" onClick={() => setEmployeeSelectOpen(true)}>
                                    <Trans>catalogSelectEmployee</Trans>
                                </Button>
                            )}
                        </div>
                    )}

                    {/* Total number - big screen only */}
                    {totalCount > 0 && (
                        <div className='ms-auto d-none d-lg-flex align-items-center'>
                            <span className='text-secondary'>{measurementId ? t("Total suitable models") : t("Total")}: {totalCount}</span> {modelsLoading ? <Spinner className='ms-1' size='sm ' /> : null}
                        </div>
                    )}

                    <div className='ms-auto'>
                        <SortButton sortMode={sortMode} setSortMode={setSortMode} ratingSortEnabled={ratingSortEnabled} />
                    </div>
                </div>

                {/* Measurement plate - small screen only */}
                <div className='mt-3 d-flex d-lg-none align-items-center'>
                    {(measurementId || measurementIds) ? (
                        <div>
                            <MeasurementFilter
                                measurementId={measurementId}
                                humanName={humanName}
                                employee={employee}
                                season={employeeSeason}
                                measurementIds={measurementIds}
                                actionCallback={toggleMeasurementList}
                                closeCallback={dismissMeasurementFilter} />
                        </div>
                    ) : (employeeApi.ready() && (
                        <div className='d-flex flex-column'>
                            {clientMeasurementApi.ready() && (
                                <>
                                    <div>
                                        <Button variant="primary" onClick={() => setMeasurementSelectOpen(true)}>
                                            <Trans>catalogSelectMeasurement</Trans>
                                        </Button>
                                    </div>
                                    <div>
                                        <Button className='mt-1' variant="primary" onClick={() => setGroupSelectOpen(true)}>
                                            <Trans>catalogSelectGroup</Trans>
                                        </Button>
                                    </div>
                                </>
                            )}

                            {employeeApi.ready() && (
                                <div>
                                    <Button className='mt-1' variant="primary" onClick={() => setEmployeeSelectOpen(true)}>
                                        <Trans>catalogSelectEmployee</Trans>
                                    </Button>
                                </div>
                            )}
                        </div>
                    ))}

                    {totalCount > 0 && (
                        <div className={'d-flex align-items-center ' + (measurementId ? 'ms-auto' : 'ms-auto me-auto')}>
                            <span className='text-secondary'>{t("Total")}: {totalCount}</span> {modelsLoading ? <Spinner className='ms-1' size='sm ' /> : null}
                        </div>
                    )}
                </div>

                {/* Measurement list */}
                {measurementIds && initMeasurementIds && (
                    <Collapse in={measurementListOpen}>
                        <div className='mt-3'>
                            <MeasurementList measurementIds={measurementIds} initMeasurementIds={initMeasurementIds} />
                        </div>
                    </Collapse>
                )}

                {/* Wide progress loader */}
                {currentTaskId && (
                    <div className='my-2 w-100'>
                        <ProgressBar animated striped variant="primary" now={currentTaskProgress * 100} />
                    </div>
                )}

                <div className='row'>
                    <div className='col-xl-3 d-none d-xl-block'>
                        <div className='mt-3'>
                            {shoeProperties ? (
                                <FiltersForm shoeProperties={shoeProperties} selected={selectedProperties} setSelected={updateSelectedProperties} defaultSelected={defaultSelected} onClear={onFiltersClear} />
                            ) : (
                                <Loader />
                            )}
                        </div>
                    </div>
                    <div className='col-xl-9'>

                        <div className="mt-3">
                            {(!isLoading && !modelsLoading) ? (
                                <>
                                    {/* Shoe models */}
                                    <div className="row mt-3">
                                        {items.length > 0 ? (
                                            items.map(item => (
                                                <div className="col-sm-6 col-md-4 col-lg-3 mb-3 d-flex align-items-stretch" key={item.modelId}>
                                                    <ShoeCard
                                                        item={item}
                                                        measurementId={measurementId}
                                                        humanName={humanName}
                                                        measurementIds={measurementIds}
                                                        cardPlayingModelId={cardPlayingModelId}
                                                        setCardPlayingModelId={setCardPlayingModelId}
                                                        allMeasurementIds={initMeasurementIds}
                                                        onContinue={onContinueMany}
                                                        onFinish={onFinishMany}
                                                    />
                                                </div>
                                            ))) : (
                                            <div className="col-12">
                                                {/* No shoe models */}
                                                <div className="text-center"><Trans>No shoe models</Trans></div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Pagination */}
                                    <PaginationBlock currentPage={currentPage} totalPages={totalPages} maxDisplayedPages={5} setCurrentPage={setCurrentPage} />
                                </>
                            ) : (
                                <div className="col-12">
                                    <Loader />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default Catalog;
